/**
 * Company settings › SolarAPP+ (`/solarapp`): connect the company's own SolarAPP+ installer account with the Client
 * ID and Client Secret made in SolarAPP+ (account menu › Client Credentials). The secret is checked with SolarAPP+,
 * stored encrypted and never shown again. While the server has the API off (no integrator access yet), the page says
 * so and explains the manual route (download the prefilled answers in the Permit workspace).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileCheck2, Plug, Unplug } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { api, errorMessage, unwrap } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, Field, PageHead, Spinner, TextInput, useToast } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { useDates } from "../lib/useDates";
import "../permits/solarapp.css";

const b = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

export function SolarAppConnection() {
  const t = useT();
  const dates = useDates();
  const orgId = useOrgId();
  const { can } = useOrg();
  const canEdit = can("settings_rules", "edit");
  const toast = useToast();
  const qc = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const state = useQuery({
    queryKey: ["solarapp-connection", orgId],
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/solarapp-connection", { params: { path: { org_id: orgId } } })),
  });
  const connect = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/orgs/{org_id}/solarapp-connection", { params: { path: { org_id: orgId } }, body: { client_id: clientId.trim(), client_secret: secret.trim() } })),
    onSuccess: (data) => {
      qc.setQueryData(["solarapp-connection", orgId], data);
      setSecret("");
      toast(t("integrations.solarapp.connected"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const disconnect = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/orgs/{org_id}/solarapp-connection", { params: { path: { org_id: orgId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["solarapp-connection", orgId] });
      toast(t("integrations.solarapp.disconnected"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const s = state.data;

  return (
    <div className="sa-connect">
      <PageHead
        title="SolarAPP+"
        description={t("integrations.solarapp.description")}
      />
      {!s ? (
        <Spinner label={t("integrations.common.loading")} />
      ) : (
        <>
          {!s.api_enabled && (
            <Alert tone="info" title={t("integrations.solarapp.notOpenTitle")}>
              {t("integrations.solarapp.notOpenBody")}
            </Alert>
          )}
          <Card
            title={t("integrations.solarapp.connection")}
            actions={
              s.connected ? (
                <Badge tone={s.status === "error" ? "danger" : "ok"}>{s.status === "error" ? t("integrations.solarapp.needsAttention") : t("integrations.solarapp.statusConnected")}</Badge>
              ) : (
                <Badge>{t("integrations.solarapp.notConnected")}</Badge>
              )
            }
          >
            {s.connected ? (
              <div className="sa-connect-state">
                <Plug size={18} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b dir="auto">{s.account_name ?? t("integrations.solarapp.account")}</b>
                  <div className="small muted">
                    {t("integrations.solarapp.clientIdEnding", { last4: s.client_id_last4 ?? "" })} · <span dir="ltr">{s.api_host}</span> ·{" "}
                    {s.connected_by_name
                      ? t("integrations.solarapp.connectedOnBy", { date: dates.date(s.connected_at), name: s.connected_by_name })
                      : t("integrations.solarapp.connectedOn", { date: dates.date(s.connected_at) })}
                  </div>
                  {s.last_error && <div className="small" dir="auto" style={{ color: "var(--danger)" }}>{s.last_error}</div>}
                </div>
                {canEdit && (
                  <Button variant="ghost" icon={<Unplug size={16} />} loading={disconnect.isPending} onClick={() => disconnect.mutate()}>
                    {t("integrations.solarapp.disconnect")}
                  </Button>
                )}
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  connect.mutate();
                }}
              >
                <Field label={t("integrations.solarapp.clientId")}>
                  <TextInput value={clientId} dir="ltr" onChange={(e) => setClientId(e.target.value)} autoComplete="off" disabled={!canEdit || !s.api_enabled} />
                </Field>
                <Field label={t("integrations.solarapp.clientSecret")} hint={t("integrations.solarapp.clientSecretHint")}>
                  <TextInput type="password" dir="ltr" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off" disabled={!canEdit || !s.api_enabled} />
                </Field>
                <Button variant="primary" type="submit" icon={<Plug size={16} />} loading={connect.isPending} disabled={!canEdit || !s.api_enabled || !clientId.trim() || !secret.trim()}>
                  {t("integrations.solarapp.connect")}
                </Button>
                {!canEdit && <p className="small muted">{t("integrations.solarapp.askAdmin")}</p>}
              </form>
            )}
          </Card>
          <Card title={t("integrations.solarapp.howTitle")}>
            <ol className="sa-steps">
              <li>
                <FormattedMessage id="integrations.solarapp.how1" values={{ b }} />{" "}
                <a href="https://help.gosolarapp.org/article/81-basic-account-help-registering-a-new-account" target="_blank" rel="noreferrer noopener">
                  {t("integrations.solarapp.registering")} <DirIcon><ExternalLink size={11} /></DirIcon>
                </a>
              </li>
              <li>
                <FormattedMessage id="integrations.solarapp.how2" values={{ b }} />
              </li>
              <li>{t("integrations.solarapp.how3")}</li>
            </ol>
            <p className="small muted" style={{ marginBottom: 0 }}>
              <FileCheck2 size={13} style={{ verticalAlign: -2 }} /> {t("integrations.solarapp.then")}
            </p>
          </Card>
        </>
      )}
    </div>
  );
}
